/**
 * Office.js adapter for the doc-embedded provenance manifest.
 *
 * Thin Promise wrapper over ``Office.context.document.customXmlParts``
 * for the Asthra **provenance** namespace. Modelled directly after
 * ``runManifest.office.ts`` (same fail-soft pattern, same callback
 * shape). All XML schema knowledge lives on the Python side; this
 * file is a transport.
 *
 * # DECISION: Mirror runManifest.office.ts patterns exactly (Promise
 * #   wrappers around the asyncResult API, return null/false in
 * #   non-Office environments, no exceptions ever leak).
 * # WHY: Two adapters touching the same Office.js subsystem should
 * #      fail the same way. Reduces cognitive load when debugging
 * #      "why didn't the manifest update" issues — one mental model
 * #      covers both manifests.
 * # DATE: 2026-05-28
 *
 * # DECISION: No unit tests for this module (same rationale as
 * #   runManifest.office.ts — Office.js only loads inside Word).
 * # WHY: see runManifest.office.ts header DECISION.
 * # DATE: 2026-05-28
 */

import { PROVENANCE_NAMESPACE, looksLikeProvenanceManifest } from "./provenanceManifest";

// ---------------------------------------------------------------------------
// Environment probe — Office.js available?
// ---------------------------------------------------------------------------

function isOfficeAvailable(): boolean {
  try {
    return typeof Office !== "undefined" && !!Office?.context?.document?.customXmlParts;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

/**
 * Read the doc-embedded provenance manifest XML, if any. Resolves
 * to ``null`` when:
 *   - Office.js is not available
 *   - no CustomXmlPart in the provenance namespace exists
 *   - the part exists but doesn't look like our schema
 *   - the Office.js call errored
 *
 * Never rejects. Returns the raw XML string for the bridge to
 * forward to Python (which owns parsing).
 */
export async function readProvenanceXml(): Promise<string | null> {
  if (!isOfficeAvailable()) return null;
  try {
    const parts = await getPartsByNamespace();
    if (parts.length === 0) return null;
    const xml = await getXmlOf(parts[0]);
    if (!looksLikeProvenanceManifest(xml)) {
      logDev("readProvenanceXml: part did not match schema", null);
      return null;
    }
    return xml;
  } catch (err) {
    logDev("readProvenanceXml fetch failed", err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------

/**
 * Replace the doc-embedded provenance manifest with this XML.
 * Removes any existing parts in the provenance namespace first
 * (so the document doesn't accumulate stale manifests across
 * regenerations). Returns ``true`` on success, ``false`` on any
 * failure (including non-Office env).
 *
 * Caller (the bridge handler) is responsible for ensuring ``xml``
 * is a serialisable provenance manifest produced by the Python
 * encoder. We do a minimal looks-like check to refuse obvious
 * mistakes (e.g. passing the wrong namespace).
 *
 * Never rejects.
 */
export async function writeProvenanceXml(xml: string): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  if (!looksLikeProvenanceManifest(xml)) {
    logDev("writeProvenanceXml: refused, XML did not look like a provenance manifest", null);
    return false;
  }
  try {
    await clearProvenanceXml();
    await addPart(xml);
    return true;
  } catch (err) {
    logDev("writeProvenanceXml failed", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Clear
// ---------------------------------------------------------------------------

/**
 * Remove every provenance-namespace CustomXmlPart from the document.
 * Returns ``true`` on success (including "nothing to remove"), ``false``
 * on any Office.js error. Never rejects.
 */
export async function clearProvenanceXml(): Promise<boolean> {
  if (!isOfficeAvailable()) return false;
  try {
    const parts = await getPartsByNamespace();
    if (parts.length === 0) return true;
    await Promise.all(parts.map((p) => deletePart(p)));
    return true;
  } catch (err) {
    logDev("clearProvenanceXml failed", err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Office.js callback wrappers — Promise-ify the async-result pattern
// ---------------------------------------------------------------------------

function getPartsByNamespace(): Promise<Office.CustomXmlPart[]> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.customXmlParts.getByNamespaceAsync(PROVENANCE_NAMESPACE, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value ?? []);
        } else {
          reject(result.error ?? new Error("getByNamespaceAsync failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

function getXmlOf(part: Office.CustomXmlPart): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      part.getXmlAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value ?? null);
        } else {
          logDev("part.getXmlAsync failed", result.error);
          resolve(null);
        }
      });
    } catch (err) {
      logDev("part.getXmlAsync threw", err);
      resolve(null);
    }
  });
}

function addPart(xml: string): Promise<Office.CustomXmlPart | null> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.document.customXmlParts.addAsync(xml, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value ?? null);
        } else {
          reject(result.error ?? new Error("customXmlParts.addAsync failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

function deletePart(part: Office.CustomXmlPart): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      part.deleteAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(result.error ?? new Error("part.deleteAsync failed"));
        }
      });
    } catch (err) {
      reject(err);
    }
  });
}

// ---------------------------------------------------------------------------
// Dev logging — silent in production, visible in dev console
// ---------------------------------------------------------------------------

function logDev(label: string, err: unknown): void {
  if (typeof console === "undefined") return;
  console.warn(`[provenanceManifest.office] ${label}:`, err);
}
